import React, { useState } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  Phone,
  Mail,
  Handshake,
  MessageSquare,
  StickyNote,
  CalendarClock,
  Flag,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

// Outreach log + Met-in-person tracker rendered inside the realtor edit
// dialog. Every interaction (call/text/email/DM/etc.) posts an
// `outreach_log` entry and updates the realtor doc; the parent binds a
// callback (`onUpdated`) to keep local state in sync with the returned
// realtor doc so the timeline UI + last-touch-at chips stay accurate.

const OUTREACH_TYPES = [
  { value: "met_in_person", label: "Met in person", icon: Handshake },
  { value: "call", label: "Phone call", icon: Phone },
  { value: "text", label: "Text message", icon: MessageSquare },
  { value: "email", label: "Email", icon: Mail },
  { value: "dm", label: "Social DM", icon: MessageSquare },
  { value: "left_card", label: "Left business card", icon: StickyNote },
  { value: "note", label: "Note", icon: StickyNote },
];

export function formatRelativeDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return "today";
    if (diffDays === 1) return "yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function OutreachLogPanel({ realtor, onUpdated, flushDirtyEdits }) {
  const [entryType, setEntryType] = useState("note");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const log = Array.isArray(realtor.outreach_log) ? realtor.outreach_log : [];

  const toggleMet = async () => {
    setBusy(true);
    try {
      // Save any pending profile edits (notes/tags/phone/...) first so the
      // user doesn't lose them when they tap Met-in-person.
      if (flushDirtyEdits) await flushDirtyEdits();
      if (realtor.met_in_person_at) {
        const { data } = await api.post(`/realtors/${realtor.id}/met`, { clear: true });
        toast.success("Cleared met flag");
        onUpdated(data);
      } else {
        const { data } = await api.post(`/realtors/${realtor.id}/met`, { note: "" });
        toast.success("Saved", { description: "Marked as met today — notes saved." });
        onUpdated(data);
      }
    } catch (e) {
      toast.error("Update failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  // Toggle the "flag for future follow-up" pin. A flagged realtor rises
  // to the top of the outreach queue (after today's 20) and gets a small
  // red flag badge on their row. Logging any real touch on the timeline
  // (call/sms/email/dm/note/referral) clears the flag automatically.
  const toggleFlag = async () => {
    setBusy(true);
    try {
      if (flushDirtyEdits) await flushDirtyEdits();
      const { data } = await api.post(`/realtors/${realtor.id}/flag`);
      toast.success(
        data.flagged_at ? "Flagged for follow-up" : "Follow-up flag cleared",
      );
      onUpdated(data);
    } catch (e) {
      toast.error("Couldn't toggle flag", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const addEntry = async () => {
    if (!note.trim() && entryType === "note") {
      toast.error("Add a note before saving");
      return;
    }
    setBusy(true);
    try {
      if (flushDirtyEdits) await flushDirtyEdits();
      const { data } = await api.post(`/realtors/${realtor.id}/outreach`, {
        type: entryType,
        note: note.trim(),
      });
      toast.success("Outreach logged");
      setNote("");
      onUpdated(data);
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (entryId) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/realtors/${realtor.id}/outreach/${entryId}`);
      toast.success("Entry removed");
      onUpdated(data);
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="border-t border-slate-200 mt-4 pt-4 space-y-3"
      data-testid="outreach-log-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2 font-display">
          <CalendarClock className="h-4 w-4 text-slate-500" /> Outreach timeline
        </h4>
        <div className="flex items-center gap-2">
          <Button
            variant={realtor.flagged_at ? "default" : "outline"}
            size="sm"
            onClick={toggleFlag}
            disabled={busy}
            className={`min-h-[40px] px-2.5 ${
              realtor.flagged_at
                ? "bg-rose-600 hover:bg-rose-700 text-white"
                : "text-rose-700 hover:bg-rose-50 border-rose-300"
            }`}
            title={realtor.flagged_at ? "Clear follow-up flag" : "Flag for future follow-up"}
            aria-label={realtor.flagged_at ? "Clear follow-up flag" : "Flag for future follow-up"}
            data-testid="toggle-flag-button"
          >
            <Flag className={`h-4 w-4 ${realtor.flagged_at ? "fill-current" : ""}`} />
          </Button>
          <Button
            variant={realtor.met_in_person_at ? "default" : "outline"}
            size="sm"
            onClick={toggleMet}
            disabled={busy}
            className={`min-h-[40px] ${
              realtor.met_in_person_at
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : ""
            }`}
            data-testid="toggle-met-in-person-button"
          >
            <Handshake className="h-4 w-4 mr-2" />
            {realtor.met_in_person_at
              ? `Met ${formatRelativeDate(realtor.met_in_person_at)}`
              : "Met Today"}
          </Button>
        </div>
      </div>

      {/* Add entry */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={entryType} onValueChange={setEntryType}>
            <SelectTrigger
              className="min-h-[44px] sm:w-44"
              data-testid="outreach-type-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OUTREACH_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note (what was discussed?)"
            className="min-h-[44px] flex-1"
            data-testid="outreach-note-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                addEntry();
              }
            }}
          />
          <Button
            onClick={addEntry}
            disabled={busy}
            className="text-white min-h-[44px]"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="outreach-add-button"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Log
          </Button>
        </div>
      </div>

      {/* Timeline */}
      {log.length === 0 ? (
        <p className="text-xs text-slate-500 italic px-1">
          No outreach logged yet. Use the form above to track every interaction.
        </p>
      ) : (
        <ul className="space-y-2 max-h-60 overflow-y-auto pr-1">
          {[...log]
            .sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1))
            .map((e) => {
              const meta =
                OUTREACH_TYPES.find((t) => t.value === e.type) || OUTREACH_TYPES[6];
              const Icon = meta.icon;
              return (
                <li
                  key={e.id}
                  className="flex items-start gap-2 text-sm border border-slate-200 rounded-md p-2 bg-white"
                  data-testid={`outreach-entry-${e.id}`}
                >
                  <Icon className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">{meta.label}</span>
                      <span className="text-xs text-slate-500 tabular-nums">
                        {formatRelativeDate(e.timestamp)}
                      </span>
                      {e.actor_email ? (
                        <span className="text-xs text-slate-400 truncate">
                          · {e.actor_email}
                        </span>
                      ) : null}
                    </div>
                    {e.note ? (
                      <div className="text-slate-700 text-sm mt-0.5 whitespace-pre-wrap">
                        {e.note}
                      </div>
                    ) : null}
                  </div>
                  <button
                    onClick={() => removeEntry(e.id)}
                    disabled={busy}
                    className="text-slate-400 hover:text-red-600 p-1"
                    aria-label="Remove entry"
                    data-testid={`outreach-delete-${e.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
        </ul>
      )}
    </section>
  );
}
